import { useEffect, useMemo, useState } from 'react'
import type { FormEvent } from 'react'

import { api } from '../api/client'
import type { AuthMode, Credential, CredentialField, CredentialKind, Probe, ProxyScelta } from '../api/types'
import { Card, EmptyState, ErrorBanner, formatDateTime, Spinner } from './common'
import { DataTable } from './DataTable'
import { GuidaAws } from './GuidaAws'
import { GuidaFortinet } from './GuidaFortinet'
import { GuidaCredenziale } from './GuidaCredenziale'
import { GuidaHyperV } from './GuidaHyperV'
import { SceltaProxy, proxyIniziale, proxyPayload } from './SceltaProxy'
import { Spiegazione } from './Spiegazione'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'
import { usePermessiModuli } from '../auth/permessi'
import { colonnaTag, useTagDi } from './Tag'

/**
 * Le categorie delle credenziali di discovery. Decidono chi le vede: nei ruoli
 * personalizzati ogni categoria è un sottomodulo di «Credenziali» (vedi
 * control-plane/app/services/permessi_service.py). Le sorgenti live non ne hanno.
 */
function categorie(): { value: string; label: string }[] {
  return [
    { value: 'rete', label: t('Rete') },
    { value: 'server', label: t('Server') },
    { value: 'virtualizzazione', label: t('Virtualizzazione') },
    { value: 'cloud', label: t('Cloud') },
    { value: 'storage', label: t('Storage') },
    { value: 'database', label: t('Database') },
  ]
}

function etichettaCategoria(c: string | null | undefined): string {
  return categorie().find((x) => x.value === c)?.label ?? '—'
}

/** Come presentare la robustezza di una modalità. */
const SECURITY_LABEL: Record<string, () => string> = {
  preferred: () => t('consigliata'),
  acceptable: () => t('accettabile'),
  discouraged: () => t('sconsigliata'),
}

/**
 * Creare, modificare ed eliminare le credenziali di **alcuni** tipi: la pagina
 * Credenziali mostra quelle di discovery (SNMP, SSH, WinRM, virtualizzazione…),
 * ogni modulo (Backup, IPAM, DNS, Wazuh, Firewall) le sue, nella sezione
 * «Connessioni». Stesso form e stesse regole ovunque; cambia solo `filtro`.
 */
export function GestioneCredenziali({
  filtro,
  titolo,
  segnaposto,
  onChanged,
}: {
  /** Quali tipi del catalogo si gestiscono qui. */
  filtro: (k: CredentialKind) => boolean
  /** Il titolo della card del form (default «Nuova credenziale»). */
  titolo?: string
  /** Il nome d'esempio nel campo «Nome». */
  segnaposto?: string
  /** Dopo una creazione, modifica o eliminazione (per chi tiene dati collegati). */
  onChanged?: () => void
}) {
  const tutte = useAsync(() => api.credentials(), [])
  const catalogo = useAsync(() => api.credentialKinds(), [])
  const license = useAsync(() => api.license(), [])
  const probes = useAsync(() => api.probes(), [])
  const visibili = useMemo(() => (catalogo.data ?? []).filter(filtro), [catalogo.data]) // eslint-disable-line react-hooks/exhaustive-deps
  const kinds = { ...catalogo, data: catalogo.data ? visibili : null }
  const tipiVisibili = useMemo(() => new Set(visibili.map((k) => k.kind)), [visibili])
  const credentials = {
    ...tutte,
    data: tutte.data ? tutte.data.filter((c) => tipiVisibili.has(c.kind)) : null,
  }

  const [name, setName] = useState('')
  const [kind, setKind] = useState('')
  // La categoria della nuova credenziale (quella del tipo, cambiabile) e il
  // filtro per categoria dell'elenco.
  const [categoria, setCategoria] = useState('')
  const [filtroCategoria, setFiltroCategoria] = useState('')
  const permessi = usePermessiModuli()
  // I tag delle credenziali, nella colonna «Tag» (filtrabile, modificabile sul posto).
  const tagCredenziali = useTagDi('credential')
  const categorieScrivibili = categorie().filter(
    (c) => permessi.scrive('credentials') && permessi.scrive(`cred_${c.value}`),
  )
  // Il primo tipo gestito qui, appena il catalogo arriva.
  useEffect(() => {
    if (!kind && visibili.length) {
      // SNMP v2c resta il default dove c'è (la pagina Credenziali): è il più
      // comune. Altrimenti il primo tipo **compreso nel piano**: un tipo non
      // licenziato è disabilitato nel menu, e se fosse il default resterebbe
      // selezionato senza che nessuno l'abbia scelto.
      const f = license.data?.features ?? {}
      const primo = visibili.find((k) => k.kind === 'snmp_v2c' && f[k.feature] !== false)
        ?? visibili.find((k) => f[k.feature] !== false) ?? visibili[0]
      setKind(primo.kind)
    }
  }, [visibili, kind, license.data])
  const [mode, setMode] = useState('')
  const [values, setValues] = useState<Record<string, string>>({})
  // La sonda da cui esce la query, per le sorgenti interrogate in tempo reale
  // (ntopng/Wazuh): vuoto = la prima disponibile. Salvata in `meta.appliance_id`.
  const [appliance, setAppliance] = useState('')
  // Il proxy delle chiamate della sorgente live: nessuno, suo, o della sonda.
  const [proxy, setProxy] = useState(() => proxyIniziale())
  // Da dove si parla, per le sorgenti che si possono interrogare anche dal
  // Control Plane (phpIPAM): `probe` (default) o `cloud`.
  const [esecuzione, setEsecuzione] = useState<'probe' | 'cloud'>('probe')
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)
  // Quale credenziale si sta modificando. Una alla volta: due moduli aperti su
  // due credenziali diverse invitano a scambiarsi i campi.
  const [editing, setEditing] = useState<string | null>(null)
  /** Il tipo di sistema di una credenziale, dal catalogo. */
  const specDi = (c: Credential) => kinds.data?.find((k) => k.kind === c.kind)
  /** La modalità di autenticazione salvata, con etichetta e avvertenze. */
  const modoDi = (c: Credential) =>
    specDi(c)?.modes.find((m) => m.value === String(c.meta?.auth_mode ?? ''))

  const spec: CredentialKind | undefined = kinds.data?.find((k) => k.kind === kind)
  const selectedMode: AuthMode | undefined =
    spec?.modes.find((m) => m.value === mode) ?? spec?.modes[0]

  const features = license.data?.features ?? {}
  // Un tipo non compreso nel piano resta visibile ma non selezionabile: toglierlo
  // lascerebbe a chiedersi se il connettore esista o vada acquistato.
  const licensed = (k: CredentialKind) => features[k.feature] !== false

  // Cambiando sistema, la modalità del precedente non esiste più: si riparte
  // dalla prima, che il catalogo garantisce essere la più sicura disponibile.
  useEffect(() => {
    if (!spec) return
    setMode(spec.modes[0]?.value ?? '')
    setValues({})
    setAppliance('')
    setCategoria(spec.default_category ?? '')
  }, [kind]) // eslint-disable-line react-hooks/exhaustive-deps

  const fields: CredentialField[] = useMemo(
    () => [...(spec?.common_fields ?? []), ...(selectedMode?.fields ?? [])],
    [spec, selectedMode],
  )

  // Le sonde fra cui scegliere per una sorgente live: solo quelle con la
  // capability abilitata, come nel form di discovery. Per le sorgenti live il
  // nome della funzione **è** quello della capability (ntopng→ntopng,
  // wazuh→wazuh): il connettore non deve poter uscire da una sonda che quel
  // servizio non la sa nemmeno raggiungere.
  const sondeIdonee = useMemo(
    () =>
      spec?.live
        ? (probes.data ?? []).filter((p) => p.effective_capabilities.includes(spec.feature))
        : [],
    [probes.data, spec],
  )

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!spec || !selectedMode) return
    setError(null)
    setBusy(true)
    try {
      // La modalità viaggia nel campo che il connettore legge: per SNMP v3 è
      // `security_level`, nome che il protocollo usa da sempre.
      const secret: Record<string, string> = { [spec.mode_field]: selectedMode.value }
      for (const f of fields) {
        const value = (values[f.name] ?? '').trim()
        // Un campo vuoto viene omesso: una stringa vuota non è la stessa cosa di
        // un valore non fornito.
        if (value !== '') secret[f.name] = value
      }

      // Per le sorgenti live, la sonda scelta viaggia in `meta` (non è un
      // segreto): il servizio la usa per la query real-time. Il proxy sta in
      // `meta` sia per le live sia per i kind cloud/virt (`proxy_ok`), dove la
      // sonda che esegue il job deve poter attraversare il proxy della rete.
      let meta: Record<string, unknown> | undefined
      if (spec.live) {
        meta = {
          ...(appliance && esecuzione === 'probe' ? { appliance_id: appliance } : {}),
          ...(spec.cloud_ok ? { execution: esecuzione } : {}),
          proxy: proxyPayload(proxy),
        }
      } else if (spec.proxy_ok || categoria) {
        meta = {
          ...(categoria ? { category: categoria } : {}),
          ...(spec.proxy_ok ? { proxy: proxyPayload(proxy) } : {}),
        }
      } else {
        meta = undefined
      }
      await api.createCredential({ name, kind, secret, meta })
      setProxy(proxyIniziale())
      setName('')
      setValues({})
      setAppliance('')
      tutte.reload()
      onChanged?.()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  async function remove(id: string) {
    if (!window.confirm(t('Eliminare questa credenziale? Si toglie anche dai gruppi, e i job e le sorgenti che la usano smettono di funzionare. Il segreto non si recupera: andrà riscritto.'))) return
    setError(null)
    try {
      await api.deleteCredential(id)
      tutte.reload()
      // Eliminare una credenziale la toglie dai gruppi in cascata: chi mostra
      // i gruppi (la pagina Credenziali) deve ricaricarli.
      onChanged?.()
    } catch (err) {
      setError(err)
    }
  }

  return (
    <>
      <ErrorBanner error={error ?? credentials.error ?? kinds.error} />

      <Card title={titolo ?? t('Nuova credenziale')}>
        {kinds.loading && !kinds.data ? (
          <Spinner />
        ) : (
          <form onSubmit={onSubmit}>
            <div className="row">
              <div className="field">
                <label htmlFor="name">{t('Nome')}</label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={segnaposto ?? t('snmp-readonly-sede')}
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="kind">{t('Sistema')}</label>
                <select id="kind" value={kind} onChange={(e) => setKind(e.target.value)}>
                  {kinds.data?.map((k) => (
                    <option key={k.kind} value={k.kind} disabled={!licensed(k)}>
                      {licensed(k)
                        ? t(k.label)
                        : t('{sistema} — non compreso nella licenza', { sistema: t(k.label) })}
                    </option>
                  ))}
                </select>
              </div>
              {spec && !spec.live && (
                <div className="field">
                  <label htmlFor="categoria">{t('Categoria')}</label>
                  <select id="categoria" value={categoria} onChange={(e) => setCategoria(e.target.value)}>
                    {categorie().map((c) => (
                      <option
                        key={c.value}
                        value={c.value}
                        disabled={!categorieScrivibili.some((x) => x.value === c.value)}
                      >
                        {c.label}
                      </option>
                    ))}
                  </select>
                  <p className="field__hint">
                    {t('Decide chi la vede: nei ruoli personalizzati ogni categoria si concede a parte.')}
                  </p>
                </div>
              )}
              {(spec?.modes.length ?? 0) > 1 && (
                <div className="field">
                  <label htmlFor="mode">{t('Metodo di autenticazione')}</label>
                  <select
                    id="mode"
                    value={selectedMode?.value ?? ''}
                    onChange={(e) => setMode(e.target.value)}
                  >
                    {spec?.modes.map((m) => (
                      <option key={m.value} value={m.value}>
                        {t(m.label)} ({SECURITY_LABEL[m.security]?.() ?? m.security})
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {selectedMode?.hint && (
              <div
                className={
                  selectedMode.security === 'discouraged'
                    ? 'banner banner--error'
                    : 'banner banner--info'
                }
              >
                {selectedMode.security === 'discouraged' && <strong>{t('Sconsigliata.')} </strong>}
                {t(selectedMode.hint)}
              </div>
            )}

            {fields.some((f) => f.name === 'private_key') && <GuidaChiaveSsh />}
            {spec?.kind === 'wazuh' && <GuidaWazuh />}
            {spec?.kind === 'aws' && <GuidaAws />}
            {spec?.kind === 'fortigate' && <GuidaFortinet />}
            {spec?.kind === 'hyperv' && <GuidaHyperV />}
            {spec?.guide && <GuidaCredenziale guida={spec.guide} />}

            <SecretFields
              fields={fields}
              values={values}
              onChange={(field, value) => setValues({ ...values, [field]: value })}
            />

            {spec?.live && spec.cloud_ok && (
              <SceltaEsecuzione id="esecuzione" value={esecuzione} onChange={setEsecuzione} />
            )}
            {spec?.live && esecuzione === 'probe' && (
              <div className="field">
                <label htmlFor="sonda">{t('Sonda')}</label>
                <select id="sonda" value={appliance} onChange={(e) => setAppliance(e.target.value)}>
                  <option value="">{t('La prima idonea')}</option>
                  {sondeIdonee.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <p className="field__hint">
                  {t('Sonda utilizzata come punto di origine della query in tempo reale. L’elenco include solo le sonde con capability abilitata. La sonda deve essere raggiungibile dal sistema.')}
                </p>
                {sondeIdonee.length === 0 && (
                  <p className="field__hint">
                    {t('Nessuna sonda ha questa capability abilitata: abilitala dalla scheda della sonda che raggiunge il servizio.')}
                  </p>
                )}
              </div>
            )}
            {(spec?.live || spec?.proxy_ok) && (
              <SceltaProxy
                value={proxy}
                onChange={setProxy}
                senzaSonda={spec.live ? Boolean(spec.cloud_ok) && esecuzione === 'cloud' : false}
                sonda={
                  spec.live
                    ? (sondeIdonee.find((p) => p.id === appliance) ?? (appliance ? undefined : sondeIdonee[0]))
                    : undefined
                }
              />
            )}

            {/* Palo Alto con chiave API: invece di far lanciare a mano il keygen e
                incollarne l'output, la console lo fa **via la sonda** (il firewall
                è sulla rete del cliente) e riempie il campo «Chiave API». */}
            {spec?.kind === 'paloalto' && selectedMode?.value === 'api_key' && (
              <GeneraChiavePaloAlto
                endpoint={values.endpoint ?? ''}
                applianceId={appliance}
                insecureTls={String(values.insecure_tls ?? '') === 'true'}
                onGenerated={(key) => setValues({ ...values, api_key: key })}
              />
            )}

            {spec && !licensed(spec) && (
              <div className="banner banner--warn">
                {t('Non compreso nella licenza')}
              </div>
            )}
            <button type="submit" disabled={busy || (spec ? !licensed(spec) : false)}>
              {busy ? t('Salvataggio…') : t('Crea credenziale')}
            </button>
          </form>
        )}
      </Card>

      <Card title={t('Credenziali configurate')}>
        {credentials.loading && !credentials.data ? (
          <Spinner />
        ) : !credentials.data?.length ? (
          <EmptyState
            title={t('Nessuna credenziale')}
            hint={t('Creane una per poter avviare una discovery.')}
          />
        ) : (
          // Lo stesso modulo di filtro delle altre tabelle: con decine di
          // credenziali la domanda è «quella di vCenter» o «le SNMP», e si
          // scrive nell'intestazione della colonna.
          <>
          {credentials.data.some((c) => c.category) && (
            <div className="report__filters" role="tablist" aria-label={t('Categoria')}>
              <button
                type="button"
                className={filtroCategoria === '' ? 'chip chip--active' : 'chip'}
                onClick={() => setFiltroCategoria('')}
              >
                {t('Tutte')} ({credentials.data.length})
              </button>
              {categorie().map((c) => {
                const n = credentials.data!.filter((x) => x.category === c.value).length
                return n > 0 ? (
                  <button
                    type="button"
                    key={c.value}
                    className={filtroCategoria === c.value ? 'chip chip--active' : 'chip'}
                    onClick={() => setFiltroCategoria(c.value)}
                  >
                    {c.label} ({n})
                  </button>
                ) : null
              })}
            </div>
          )}
          <DataTable
            rows={credentials.data.filter((c) => !filtroCategoria || c.category === filtroCategoria)}
            rowKey={(c) => c.id}
            initialSort={{ key: credentials.data.some((c) => c.category) ? 'categoria' : 'name' }}
            expanded={(c) => editing === c.id}
            expand={(c) => {
              const spec = specDi(c)
              return spec ? (
                <EditCredential
                  credential={c}
                  spec={spec}
                  probes={probes.data ?? []}
                  onDone={() => {
                    setEditing(null)
                    tutte.reload()
                    onChanged?.()
                  }}
                  onError={setError}
                />
              ) : null
            }}
            columns={[
              ...(credentials.data.some((c) => c.category)
                ? [{ key: 'categoria', header: t('Categoria'), value: (c: Credential) => etichettaCategoria(c.category) }]
                : []),
              { key: 'name', header: t('Nome'), value: (c) => c.name },
              ...colonnaTag<Credential>('credential', tagCredenziali, (c) => c.id),
              {
                key: 'kind',
                header: t('Sistema'),
                value: (c) => {
                  const spec = specDi(c)
                  return spec ? t(spec.label) : c.kind
                },
              },
              {
                key: 'auth',
                header: t('Autenticazione'),
                value: (c) => {
                  const modo = modoDi(c)
                  return modo ? t(modo.label) : String(c.meta?.auth_mode ?? '')
                },
                render: (c) => {
                  const modo = modoDi(c)
                  return (
                    <>
                      {modo ? t(modo.label) : String(c.meta?.auth_mode ?? '') || '—'}
                      {modo?.security === 'discouraged' && (
                        <span className="chip" title={modo.hint ? t(modo.hint) : undefined}>
                          {t('sconsigliata')}
                        </span>
                      )}
                    </>
                  )
                },
              },
              {
                key: 'created_at',
                header: t('Creata'),
                value: (c) => c.created_at,
                render: (c) => formatDateTime(c.created_at),
              },
              {
                key: 'azioni',
                header: '',
                filter: false,
                sort: false,
                render: (c) => (
                  <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button
                      className="secondary"
                      onClick={() => setEditing(editing === c.id ? null : c.id)}
                    >
                      {editing === c.id ? t('Chiudi') : t('Modifica')}
                    </button>{' '}
                    <button className="danger" onClick={() => remove(c.id)}>
                      {t('Elimina')}
                    </button>
                  </div>
                ),
              },
            ]}
          />
          </>
        )}
      </Card>

    </>
  )
}

/**
 * La guida per creare una chiave SSH, richiudibile.
 *
 * Sta accanto al campo, non in un manuale a parte: chi si trova qui davanti per
 * la prima volta è spesso chi non ha mai generato una chiave, e la domanda —
 * «cosa incollo, e come faccio a fartela accettare dall'host?» — nasce proprio
 * in questo punto. Chiusa di default per non ingombrare chi lo sa già.
 */
function GuidaChiaveSsh() {
  return (
    <details className="guida-chiave">
      <summary>{t('Come creo una chiave SSH?')}</summary>
      <div className="guida-chiave__corpo">
        <p>
          {t(
            'Una chiave è una coppia di file: uno privato, che si incolla qui, e uno pubblico (.pub) che va sull’apparato da scansionare. Non riusare la chiave con cui entri a mano: creane una dedicata alla discovery. Ci sono due strade, a seconda di dove preferisci generarla.',
          )}
        </p>

        <h4>{t('A) Genero la chiave sul mio computer e installo la pubblica')}</h4>
        <ol>
          <li>
            {t('Generala (su Linux o Mac, dal terminale):')}
            <pre>
              <code>{'ssh-keygen -t ed25519 -f censo-discovery -C censo-discovery'}</code>
            </pre>
            {t(
              'Ottieni due file nella cartella corrente: censo-discovery (privato) e censo-discovery.pub (pubblico). Se imposti una passphrase, mettila poi nel campo qui sotto.',
            )}
          </li>
          <li>
            {t('Installa la chiave pubblica sugli apparati da scansionare:')}
            <pre>
              <code>{'ssh-copy-id -i censo-discovery.pub utente@apparato'}</code>
            </pre>
            {t(
              'In alternativa, aggiungi il contenuto del file .pub in coda a ~/.ssh/authorized_keys sull’host.',
            )}
          </li>
          <li>
            {t(
              'Incolla qui il contenuto del file privato (censo-discovery, quello senza .pub): tutto, dalla riga -----BEGIN fino a -----END compresa.',
            )}
          </li>
        </ol>

        <h4>{t('B) Creo utente e chiave sul server, poi copio la privata qui')}</h4>
        <p className="field__hint">
          {t(
            'Utile quando preferisci preparare tutto sull’apparato Linux da scansionare. I comandi vanno eseguiti sul server, come root o con sudo.',
          )}
        </p>
        <ol>
          <li>
            {t('Crea un utente dedicato, di sola lettura, senza password:')}
            <pre>
              <code>
                {'sudo useradd -m -s /bin/bash censo-discovery\n' +
                  'sudo -u censo-discovery mkdir -p /home/censo-discovery/.ssh\n' +
                  'sudo -u censo-discovery chmod 700 /home/censo-discovery/.ssh'}
              </code>
            </pre>
          </li>
          <li>
            {t('Genera la coppia di chiavi nella home dell’utente:')}
            <pre>
              <code>
                {"sudo -u censo-discovery ssh-keygen -t ed25519 -N '' " +
                  '-f /home/censo-discovery/.ssh/censo-discovery'}
              </code>
            </pre>
            {t('L’opzione -N con virgolette vuote crea la chiave senza passphrase; se ne metti una, riportala poi nel campo qui sotto.')}
          </li>
          <li>
            {t('Autorizza la chiave appena creata su questo stesso server:')}
            <pre>
              <code>
                {'sudo -u censo-discovery sh -c "cat /home/censo-discovery/.ssh/censo-discovery.pub ' +
                  '>> /home/censo-discovery/.ssh/authorized_keys"\n' +
                  'sudo -u censo-discovery chmod 600 /home/censo-discovery/.ssh/authorized_keys'}
              </code>
            </pre>
          </li>
          <li>
            {t('Mostra la chiave PRIVATA per copiarla — e incollala nel campo qui sotto:')}
            <pre>
              <code>{'sudo cat /home/censo-discovery/.ssh/censo-discovery'}</code>
            </pre>
            {t(
              'Copia tutto, dalla riga -----BEGIN fino a -----END compresa. Su un parco di più server, la stessa chiave pubblica va messa in authorized_keys di censo-discovery su ognuno.',
            )}
          </li>
          <li>
            {t(
              'Facoltativo, per l’inventario dei banchi di memoria: dmidecode si legge solo da root. Concedi a questo utente il permesso, senza password, per quel solo comando — e nient’altro:',
            )}
            <pre>
              <code>
                {'echo "censo-discovery ALL=(root) NOPASSWD: /usr/sbin/dmidecode" ' +
                  '| sudo tee /etc/sudoers.d/censo-discovery\n' +
                  'sudo chmod 440 /etc/sudoers.d/censo-discovery'}
              </code>
            </pre>
            {t(
              'Senza questa riga la discovery funziona lo stesso: i banchi di memoria restano vuoti e il rapporto lo dichiara. La sonda usa sudo solo per questo comando e solo se non entra già come root. Altri pochi dati (conteggio account senza password, config effettiva di sshd) richiedono root pieno e non li concediamo a un utente di sola lettura: se ti servono, usa una credenziale con privilegi di root.',
            )}
          </li>
        </ol>

        <p className="field__hint">
          {t(
            'In entrambi i casi, verifica prima che la chiave funzioni: ssh -i censo-discovery censo-discovery@apparato deve entrare senza chiedere la password.',
          )}
        </p>
      </div>
    </details>
  )
}

/**
 * La guida per creare le credenziali Wazuh, richiudibile.
 *
 * Wazuh non ha una sola credenziale: la API del manager (55000) e l'Indexer
 * (9200) hanno utenti e sicurezza **separati**, e li si crea in due punti
 * diversi del dashboard. È esattamente la domanda che nasce qui davanti — «quale
 * utente, e dove lo faccio?» — quindi la risposta sta accanto ai campi. Chiusa
 * di default per non ingombrare chi lo sa già.
 */
function GuidaWazuh() {
  return (
    <details className="guida-chiave">
      <summary>{t('Come creo le credenziali Wazuh, e dove?')}</summary>
      <div className="guida-chiave__corpo">
        <p>
          {t(
            'Wazuh usa due accessi distinti, entrambi da creare dedicati e in sola lettura: l’utente della API del manager (porta 55000: agenti e conformità SCA) e — se vuoi anche allarmi e vulnerabilità — l’utente dell’Indexer (porta 9200), che è un’utenza separata. Le credenziali di default wazuh/wazuh vanno cambiate, non usate qui.',
          )}
        </p>

        <h4>{t('A) Utente della Wazuh API (manager, porta 55000)')}</h4>
        <p className="field__hint">
          {t(
            'Dà accesso ad agenti e SCA. Si crea nella sicurezza della Wazuh API (RBAC), che è separata da quella dell’Indexer.',
          )}
        </p>
        <ol>
          <li>
            {t(
              'Dal Wazuh dashboard: menu → Server management → Security (la sicurezza della Wazuh API). Il nome esatto della voce cambia con la versione.',
            )}
          </li>
          <li>
            {t(
              'Crea un utente dedicato e assegnagli un ruolo di sola lettura (va bene la policy di lettura predefinita): deve poter leggere agenti e SCA, niente di più.',
            )}
          </li>
          <li>
            {t('In alternativa, dalla riga di comando (cambia password amministrativa e valori del nuovo utente):')}
            <pre>
              <code>
                {"curl -sk -u wazuh:PASS_ADMIN -X POST \\\n" +
                  "  'https://WAZUH:55000/security/users' \\\n" +
                  "  -H 'Content-Type: application/json' \\\n" +
                  '  -d \'{"username":"censo-ro","password":"UnaPasswordLunga1!"}\''}
              </code>
            </pre>
          </li>
          <li>
            {t(
              'Metti quell’utente e la sua password nei campi «Utente API» e «Password API», e l’URL del manager (di norma https://indirizzo:55000) in «Endpoint API». La dashboard sta su un’altra porta: qui va l’API.',
            )}
          </li>
        </ol>

        <h4>{t('B) Utente dell’Indexer (porta 9200) — per allarmi e vulnerabilità')}</h4>
        <p className="field__hint">
          {t(
            'Opzionale: senza, la scheda mostra agente e SCA ma non allarmi né vulnerabilità (dalla 4.8 stanno nell’Indexer, non nella API). È un’utenza separata da quella della API.',
          )}
        </p>
        <ol>
          <li>
            {t(
              'Dal Wazuh dashboard: menu → Indexer management → Security → Internal users (la sicurezza di OpenSearch). Crea un utente interno dedicato.',
            )}
          </li>
          <li>
            {t(
              'Assegnagli un ruolo con permesso di LETTURA sugli indici wazuh-alerts-* e wazuh-states-vulnerabilities-*. Non serve il monitoraggio del cluster: la sola lettura dei dati basta.',
            )}
          </li>
          <li>
            {t(
              'Metti quell’utente e la sua password nei campi «Utente Indexer» e «Password Indexer», e l’URL dell’Indexer (di norma https://indirizzo:9200) in «Endpoint Indexer». Lascia vuoti questi campi se non ti servono allarmi e vulnerabilità.',
            )}
          </li>
        </ol>

        <h4>{t('C) La sonda che raggiunge Wazuh')}</h4>
        <p>
          {t(
            'La query esce in tempo reale da una sonda, non dal cloud: Wazuh vive sulla rete del cliente. La sonda deve raggiungere sia la 55000 sia la 9200 e avere la capability «Wazuh (SIEM/XDR)» abilitata (dalla sua scheda in Sonde). Poi scegli proprio quella nel campo «Sonda»: se ne indichi una che non la raggiunge, ottieni un errore chiaro invece di una risposta sbagliata.',
          )}
        </p>

        <p className="field__hint">
          {t(
            'Il certificato di Wazuh è spesso autofirmato: usa il certificato della CA o l’impronta SHA-256; «accetta qualunque certificato» solo se non hai alternative. Un endpoint scritto senza schema riceve https.',
          )}
        </p>
      </div>
    </details>
  )
}

/**
 * Rende i campi del segreto dichiarati dal Control Plane.
 *
 * Un modulo scritto a mano per ciascun connettore divergerebbe dai nomi che la
 * sonda si aspetta, e l'errore comparirebbe solo al primo utilizzo.
 */
/**
 * Genera la chiave API di un Palo Alto da utente/password, **via la sonda**
 * scelta (il firewall è sulla rete del cliente, il Control Plane non lo
 * raggiunge). Riempie il campo «Chiave API» invece di far lanciare a mano il
 * keygen e incollarne l'output. La password non viene salvata.
 */
function GeneraChiavePaloAlto({
  endpoint,
  applianceId,
  insecureTls,
  onGenerated,
}: {
  endpoint: string
  applianceId: string
  insecureTls: boolean
  onGenerated: (key: string) => void
}) {
  const [aperto, setAperto] = useState(false)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [fatto, setFatto] = useState(false)

  async function genera() {
    setBusy(true)
    setError(null)
    setFatto(false)
    try {
      const r = await api.paloaltoKeygen({
        endpoint,
        username,
        password,
        appliance_id: applianceId || undefined,
        insecure_tls: insecureTls,
      })
      onGenerated(r.api_key)
      setPassword('')
      setFatto(true)
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="field">
      <button type="button" className="secondary" onClick={() => setAperto((v) => !v)}>
        {t('Genera la chiave dalla password…')}
      </button>
      {aperto && (
        <div className="banner banner--info" style={{ marginTop: 8 }}>
          <p className="field__hint" style={{ marginTop: 0 }}>
            {t('Inserisci un amministratore del firewall: la sonda scelta genera la chiave API e la incolla qui sopra. Servono prima l’endpoint e la sonda; la password non viene salvata.')}
          </p>
          <div className="field">
            <label htmlFor="pa-keygen-user">{t('Utente')}</label>
            <input
              id="pa-keygen-user"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="off"
            />
          </div>
          <div className="field">
            <label htmlFor="pa-keygen-pass">{t('Password')}</label>
            <input
              id="pa-keygen-pass"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="off"
            />
          </div>
          <ErrorBanner error={error} />
          {fatto && (
            <p className="field__hint" style={{ color: 'var(--ok)' }}>
              {t('Chiave generata e inserita nel campo «Chiave API».')}
            </p>
          )}
          <button
            type="button"
            onClick={genera}
            disabled={busy || !endpoint || !username || !password}
          >
            {busy ? t('Genero…') : t('Genera chiave')}
          </button>
          {!endpoint && (
            <p className="field__hint">{t('Compila prima l’endpoint del firewall qui sopra.')}</p>
          )}
        </div>
      )}
    </div>
  )
}

function SecretFields({
  fields,
  values,
  onChange,
  idPrefix = '',
  requireAll = true,
}: {
  fields: CredentialField[]
  values: Record<string, string>
  onChange: (field: string, value: string) => void
  /**
   * Prefisso degli `id`. Il modulo di modifica vive **accanto** a quello di
   * creazione: senza, i due avrebbero campi con lo stesso identificativo, e
   * cliccare un'etichetta metterebbe il fuoco sul campo dell'altro modulo.
   */
  idPrefix?: string
  /**
   * In creazione i campi obbligatori lo sono davvero. In **modifica** no: il
   * valore salvato c'è già e non è leggibile, quindi pretenderlo di nuovo
   * costringerebbe a riscrivere la password per cambiare una porta — cioè
   * esattamente ciò che la modifica serve a evitare.
   */
  requireAll?: boolean
}) {
  if (fields.length === 0) return null

  const inline = fields.filter((f) => f.type === 'text' || f.type === 'password' || f.type === 'select')
  const areas = fields.filter((f) => f.type === 'textarea')
  const flags = fields.filter((f) => f.type === 'checkbox')

  return (
    <>
      {inline.length > 0 && (
        <div className="row">
          {inline.map((f) => (
            <div className="field" key={f.name}>
              <label htmlFor={idPrefix + f.name}>
                {t(f.label)}
                {!f.required && <span className="field__hint"> {t('(facoltativo)')}</span>}
              </label>
              {f.type === 'select' ? (
                <SceltaCampo field={f} id={idPrefix + f.name} value={values[f.name] ?? ''}
                  onChange={(v) => onChange(f.name, v)} required={requireAll && f.required} />
              ) : (
                <input
                  id={idPrefix + f.name}
                  type={f.type}
                  value={values[f.name] ?? ''}
                  onChange={(e) => onChange(f.name, e.target.value)}
                  placeholder={f.placeholder ? t(f.placeholder) : undefined}
                  autoComplete="off"
                  required={requireAll && f.required}
                />
              )}
              {f.hint && <p className="field__hint">{t(f.hint)}</p>}
            </div>
          ))}
        </div>
      )}

      {areas.map((f) => (
        <div className="field" key={f.name}>
          <label htmlFor={idPrefix + f.name}>
            {t(f.label)}
            {!f.required && <span className="field__hint"> {t('(facoltativo)')}</span>}
          </label>
          <textarea
            id={idPrefix + f.name}
            value={values[f.name] ?? ''}
            onChange={(e) => onChange(f.name, e.target.value)}
            rows={5}
            placeholder={f.placeholder ? t(f.placeholder) : undefined}
            required={requireAll && f.required}
            style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 12 }}
          />
          {f.hint && <p className="field__hint">{t(f.hint)}</p>}
        </div>
      ))}

      {flags.map((f) => (
        <div className="checkbox" key={f.name}>
          <input
            id={idPrefix + f.name}
            type="checkbox"
            checked={values[f.name] === 'true'}
            onChange={(e) => onChange(f.name, e.target.checked ? 'true' : '')}
          />
          <label htmlFor={idPrefix + f.name}>
            {t(f.label)}
            {f.hint && <small>{t(f.hint)}</small>}
          </label>
          {f.explain && f.explain.length > 0 && (
            <Spiegazione titolo={t(f.label)} paragrafi={f.explain} />
          )}
        </div>
      ))}
    </>
  )
}

/**
 * Un campo a scelta (menu a tendina) del catalogo delle credenziali. Un valore
 * salvato che non è fra le scelte — scritto a mano quando il campo era libero —
 * resta visibile come scelta in più, così aprire la modifica non lo cambia di
 * nascosto.
 */
function SceltaCampo({ field, id, value, onChange, required }: {
  field: CredentialField
  id: string
  value: string
  onChange: (value: string) => void
  required: boolean
}) {
  const scelte = field.options ?? []
  const fuori = value !== '' && !scelte.some(([v]) => v === value)
  return (
    <select id={id} value={value} onChange={(e) => onChange(e.target.value)} required={required}>
      {fuori && <option value={value}>{t('{v} (valore salvato)', { v: value })}</option>}
      {scelte.map(([v, etichetta]) => (
        <option key={v} value={v}>{t(etichetta)}</option>
      ))}
    </select>
  )
}

/**
 * Modifica di una credenziale già salvata.
 *
 * ## Perché non è «ricreala»
 *
 * Prima si poteva solo creare ed eliminare, e correggere una porta sbagliata
 * significava rifare tutto — **password compresa**, cioè la parte che chi
 * corregge un endpoint non ha sottomano: sta su un altro sistema, o in testa a
 * un'altra persona. E ricreare rompe i gruppi che nominavano quella credenziale,
 * perché eliminarla la toglie in cascata.
 *
 * ## Perché i campi segreti partono vuoti
 *
 * Password, token e chiavi private non escono dall'API in nessun caso, quindi
 * qui non c'è niente da precompilare. Lasciare il campo vuoto **non li cancella**:
 * l'aggiornamento è parziale e ciò che non si invia resta. Il modulo lo dice
 * dove serve saperlo, cioè accanto al campo, perché è la sola cosa che
 * distingue questo modulo da quello di creazione.
 */
function EditCredential({
  credential,
  spec,
  probes,
  onDone,
  onError,
}: {
  credential: Credential
  spec: CredentialKind
  probes: Probe[]
  onDone: () => void
  onError: (e: unknown) => void
}) {
  const saved = credential.fields ?? {}
  const [name, setName] = useState(credential.name)
  // La sonda scelta per una sorgente live (ntopng/Wazuh): prima si poteva solo
  // in creazione, e correggerla voleva dire rifare la credenziale. Precompilata
  // con quella salvata; vuoto = la prima idonea.
  const [appliance, setAppliance] = useState(String(credential.meta?.appliance_id ?? ''))
  const [proxy, setProxy] = useState(() => proxyIniziale(credential.meta?.proxy as ProxyScelta | undefined))
  const [esecuzione, setEsecuzione] = useState<'probe' | 'cloud'>(
    credential.meta?.execution === 'cloud' ? 'cloud' : 'probe',
  )
  const sondeIdonee = spec.live
    ? probes.filter((p) => p.effective_capabilities.includes(spec.feature))
    : []
  // La modalità salvata, non la prima del catalogo: riaprire su un'altra
  // cambierebbe metodo di autenticazione senza che nessuno l'abbia chiesto.
  const [mode, setMode] = useState(
    String(credential.meta?.auth_mode ?? saved[spec.mode_field] ?? spec.modes[0]?.value ?? ''),
  )
  const [values, setValues] = useState<Record<string, string>>({ ...saved })
  const [categoria, setCategoria] = useState(credential.category ?? spec.default_category ?? '')
  const permessi = usePermessiModuli()
  const [busy, setBusy] = useState(false)

  const selectedMode = spec.modes.find((m) => m.value === mode) ?? spec.modes[0]
  const fields = [...spec.common_fields, ...(selectedMode?.fields ?? [])]
  const secretFields = fields.filter((f) => f.type === 'password' || f.type === 'textarea')

  async function save(e: FormEvent) {
    e.preventDefault()
    onError(null)
    setBusy(true)
    try {
      const secret: Record<string, string | null> = { [spec.mode_field]: selectedMode?.value ?? '' }
      for (const f of fields) {
        const value = (values[f.name] ?? '').trim()
        const isSecret = f.type === 'password' || f.type === 'textarea'
        if (value !== '') {
          secret[f.name] = value
        } else if (isSecret) {
          // Vuoto su un campo segreto significa «non la sto cambiando»:
          // omesso, il Control Plane conserva quello salvato.
          continue
        } else if (saved[f.name] !== undefined && !f.required) {
          // Vuoto su un campo visibile che aveva un valore significa invece
          // «toglilo»: è l'unico modo per rimuovere, per esempio, un'impronta
          // TLS non più valida.
          secret[f.name] = null
        }
      }
      // Per le sorgenti live la sonda scelta viaggia in `meta`; si manda sempre
      // (anche vuota) così la si può anche azzerare, e il Control Plane fonde il
      // resto del meta (es. `auth_mode`) senza perderlo.
      let meta: Record<string, unknown> | undefined
      if (spec.live) {
        meta = {
          appliance_id: esecuzione === 'probe' ? appliance : '',
          ...(spec.cloud_ok ? { execution: esecuzione } : {}),
          proxy: proxyPayload(proxy),
        }
      } else if (spec.proxy_ok || categoria) {
        meta = {
          ...(categoria ? { category: categoria } : {}),
          ...(spec.proxy_ok ? { proxy: proxyPayload(proxy) } : {}),
        }
      } else {
        meta = undefined
      }
      await api.updateCredential(credential.id, { name: name.trim(), secret, meta })
      onDone()
    } catch (err) {
      onError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={save} className="kindform">
      <h3 className="kindform__title">{t('Modifica «{nome}»', { nome: credential.name })}</h3>

      <div className="row">
        <div className="field">
          <label htmlFor={`edit-name-${credential.id}`}>{t('Nome')}</label>
          <input
            id={`edit-name-${credential.id}`}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        {!spec.live && (
          <div className="field">
            <label htmlFor={`edit-cat-${credential.id}`}>{t('Categoria')}</label>
            <select
              id={`edit-cat-${credential.id}`}
              value={categoria}
              onChange={(e) => setCategoria(e.target.value)}
            >
              {categorie().map((c) => (
                <option
                  key={c.value}
                  value={c.value}
                  disabled={!(permessi.scrive('credentials') && permessi.scrive(`cred_${c.value}`))}
                >
                  {c.label}
                </option>
              ))}
            </select>
          </div>
        )}
        {spec.modes.length > 1 && (
          <div className="field">
            <label htmlFor={`edit-mode-${credential.id}`}>{t('Metodo di autenticazione')}</label>
            <select
              id={`edit-mode-${credential.id}`}
              value={selectedMode?.value ?? ''}
              onChange={(e) => setMode(e.target.value)}
            >
              {spec.modes.map((m) => (
                <option key={m.value} value={m.value}>
                  {t(m.label)} ({SECURITY_LABEL[m.security]?.() ?? m.security})
                </option>
              ))}
            </select>
            <p className="field__hint">
              {t('Cambiandolo servono i campi del nuovo metodo: quelli del precedente restano salvati ma non vengono più usati.')}
            </p>
          </div>
        )}
      </div>

      {/* Il sistema non si cambia: una credenziale che cambia tipo avrebbe un
          segreto con i campi di un altro connettore, e i job che la
          referenziano continuerebbero a puntarci senza che nulla lo segnali. */}
      <p className="field__hint">
        {t('Sistema: {sistema} — non modificabile. Per un altro sistema serve una credenziale nuova.', {
          sistema: t(spec.label),
        })}
      </p>

      {secretFields.length > 0 && (
        <div className="banner banner--info">
          {secretFields.length === 1
            ? t('Il campo {campi} parte vuoto: i segreti non sono leggibili. Lasciandolo vuoto resta quello salvato; si cambia solo scrivendoci sopra.', {
                campi: t(secretFields[0].label),
              })
            : t('I campi {campi} partono vuoti: i segreti non sono leggibili. Lasciandoli vuoti restano quelli salvati; si cambiano solo scrivendoci sopra.', {
                campi: secretFields.map((f) => t(f.label)).join(', '),
              })}
        </div>
      )}

      <SecretFields
        fields={fields}
        values={values}
        onChange={(field, value) => setValues({ ...values, [field]: value })}
        idPrefix={`edit-${credential.id}-`}
        requireAll={false}
      />

      {spec.live && spec.cloud_ok && (
        <SceltaEsecuzione id={`edit-esecuzione-${credential.id}`} value={esecuzione} onChange={setEsecuzione} />
      )}
      {spec.live && esecuzione === 'probe' && (
        <div className="field">
          <label htmlFor={`edit-sonda-${credential.id}`}>{t('Sonda')}</label>
          <select
            id={`edit-sonda-${credential.id}`}
            value={appliance}
            onChange={(e) => setAppliance(e.target.value)}
          >
            <option value="">{t('La prima idonea')}</option>
            {sondeIdonee.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <p className="field__hint">
            {t('Sonda utilizzata come punto di origine della query in tempo reale. L’elenco include solo le sonde con capability abilitata. La sonda deve essere raggiungibile dal sistema.')}
          </p>
          {sondeIdonee.length === 0 && (
            <p className="field__hint">
              {t('Nessuna sonda ha questa capability abilitata: abilitala dalla scheda della sonda che raggiunge il servizio.')}
            </p>
          )}
        </div>
      )}
      {(spec.live || spec.proxy_ok) && (
        <SceltaProxy
          value={proxy}
          onChange={setProxy}
          senzaSonda={spec.live ? Boolean(spec.cloud_ok) && esecuzione === 'cloud' : false}
          salvata={Boolean((credential.meta?.proxy as ProxyScelta | undefined)?.has_password)}
          sonda={
            spec.live
              ? (sondeIdonee.find((p) => p.id === appliance) ?? (appliance ? undefined : sondeIdonee[0]))
              : undefined
          }
        />
      )}

      <div className="toolbar">
        <button type="submit" disabled={busy}>
          {busy ? t('Salvataggio…') : t('Salva modifiche')}
        </button>
      </div>
    </form>
  )
}

/**
 * Da dove si interroga una sorgente che lo permette (phpIPAM): da una sonda,
 * quando sta nella rete del cliente, o dal Control Plane, quando è raggiungibile
 * da Internet. Salvato in `meta.execution`.
 */
function SceltaEsecuzione({
  id,
  value,
  onChange,
}: {
  id: string
  value: 'probe' | 'cloud'
  onChange: (v: 'probe' | 'cloud') => void
}) {
  return (
    <div className="field">
      <label htmlFor={id}>{t('Da dove si parla')}</label>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value as 'probe' | 'cloud')}>
        <option value="probe">{t('Da una sonda')}</option>
        <option value="cloud">{t('Dal Control Plane')}</option>
      </select>
      <p className="field__hint">
        {t('Da una sonda se il sistema sta nella rete del cliente; dal Control Plane se è raggiungibile da Internet (con un certificato valido).')}
      </p>
    </div>
  )
}
